import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import dayjs from 'dayjs'
import { Row, Col, DatePicker, Select, Input, InputNumber, Table, Spin, Empty, Tag, Typography } from 'antd'
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    WalletOutlined,
    ShoppingCartOutlined,
    TeamOutlined,
    UserAddOutlined,
    ShoppingOutlined,
    GiftOutlined,
    PercentageOutlined,
} from '@ant-design/icons'
import {
    ResponsiveContainer,
    AreaChart, Area,
    BarChart, Bar,
    PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts'
import controller from './orders-analytics.controller'
import './orders-analytics.less'

// Фиксированный порядок категориальных цветов для разрезов (источник,
// доставка, оплата, филиал) -- тот же набор тонов, что в плашках ниже,
// плюс пара дополнительных. Красный сюда не включаем: он зарезервирован
// за статусом "отменено", чтобы не путать разрез с "плохим" значением.
const PALETTE = ['#7264D6', '#1677ff', '#52c41a', '#fa8c16', '#13c2c2', '#eb2f96']

const WEEKDAY_LABEL: Record<number, string> = {
    1: 'Пн', 2: 'Вт', 3: 'Ср', 4: 'Чт', 5: 'Пт', 6: 'Сб', 7: 'Вс',
}

const money = (v: number) => (v ?? 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 })

const StatTile = ({ icon, tone, value, label }: { icon: React.ReactNode, tone: string, value: string | number, label: string }) => (
    <div className="oa-stat">
        <span className={`oa-stat__icon oa-stat__icon--${tone}`}>{icon}</span>
        <span className="oa-stat__value">{value}</span>
        <span className="oa-stat__label">{label}</span>
    </div>
)

const ChartCard = ({ title, children, empty }: { title: string, children: React.ReactNode, empty?: boolean }) => (
    <div className="oa-card">
        <div className="oa-card__head"><h3 className="oa-card__title">{title}</h3></div>
        {empty ? <Empty description="Нет данных за период" /> : (
            <ResponsiveContainer width="100%" height={280}>
                {children as any}
            </ResponsiveContainer>
        )}
    </div>
)

export const OrdersAnalytics = observer(() => {
    useEffect(() => {
        controller.fetch()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const data = controller.data
    const t = data?.totals

    const byDay = (data?.by_day ?? []).map(p => ({ ...p, day: dayjs(p.day).format('DD.MM') }))
    const byWeekday = (data?.by_weekday ?? []).map(p => ({ ...p, label: WEEKDAY_LABEL[p.weekday] ?? p.weekday }))

    return (
        <div className="orders-analytics">
            <Row justify="space-between" align="middle">
                <Col>
                    <Typography.Title level={3} style={{ margin: 0 }}>Аналитика заказов</Typography.Title>
                </Col>
                <Col>{controller.loading && <Spin />}</Col>
            </Row>

            <div className="orders-analytics__filters">
                <DatePicker.RangePicker
                    defaultValue={[dayjs(controller.dateFrom), dayjs(controller.dateTo)]}
                    format="DD.MM.YYYY"
                    onChange={(_, strings) => {
                        if (strings[0] && strings[1]) {
                            controller.setDateRange(
                                dayjs(strings[0], 'DD.MM.YYYY').format('YYYY-MM-DD'),
                                dayjs(strings[1], 'DD.MM.YYYY').format('YYYY-MM-DD'),
                            )
                        }
                    }}
                />
                <Select
                    style={{ minWidth: 180 }}
                    placeholder="Доставка/самовывоз"
                    allowClear
                    onChange={(v) => controller.setDeliveryType(v as number | undefined)}
                    options={[
                        { value: 1, label: 'Доставка' },
                        { value: 2, label: 'Самовывоз' },
                    ]}
                />
                <Input
                    style={{ width: 160 }}
                    placeholder="Источник (ios/android/site)"
                    allowClear
                    onChange={(e) => controller.setSource(e.target.value)}
                />
                <InputNumber placeholder="ID филиала" onChange={(v) => controller.setPlaceId(v as number | undefined)} />
                <InputNumber placeholder="ID склада" onChange={(v) => controller.setWarehouseId(v as number | undefined)} />
                <InputNumber placeholder="ID способа оплаты" onChange={(v) => controller.setPaymentTypeId(v as number | undefined)} />
            </div>

            {controller.error && <Empty description={controller.error} />}

            <div className="oa-section-title">Основные показатели</div>
            <div className="oa-stats">
                <StatTile icon={<CheckCircleOutlined />} tone="green" value={t?.completed_count ?? 0} label="Завершено заказов" />
                <StatTile icon={<CloseCircleOutlined />} tone="red" value={t?.cancelled_count ?? 0} label="Отменено заказов" />
                <StatTile icon={<WalletOutlined />} tone="violet" value={money(t?.sum ?? 0)} label="Сумма заказов" />
                <StatTile icon={<ShoppingCartOutlined />} tone="blue" value={money(t?.avg_check ?? 0)} label="Средний чек" />
                <StatTile icon={<TeamOutlined />} tone="cyan" value={t?.unique_customers ?? 0} label="Уникальных клиентов" />
                <StatTile icon={<UserAddOutlined />} tone="orange" value={t?.new_customers ?? 0} label="Новых клиентов" />
            </div>

            <div className="oa-section-title">Бонусы и скидки</div>
            <div className="oa-stats">
                <StatTile icon={<ShoppingOutlined />} tone="violet" value={(t?.avg_items_per_order ?? 0).toFixed(1)} label="Товаров на заказ" />
                <StatTile icon={<GiftOutlined />} tone="orange" value={money(t?.bonus_awarded ?? 0)} label="Баллов начислено" />
                <StatTile icon={<GiftOutlined />} tone="orange" value={money(t?.avg_bonus_awarded ?? 0)} label="Ср. начислено / заказ" />
                <StatTile icon={<WalletOutlined />} tone="blue" value={money(t?.bonus_spent ?? 0)} label="Баллов списано" />
                <StatTile icon={<PercentageOutlined />} tone="magenta" value={money(t?.total_discount_amount ?? 0)} label="Сумма скидок" />
                <StatTile icon={<PercentageOutlined />} tone="magenta" value={money(t?.avg_discount_per_order ?? 0)} label="Ср. скидка / заказ" />
            </div>

            <Row gutter={16}>
                <Col span={12}>
                    <ChartCard title="Заказы по дням" empty={byDay.length === 0}>
                        <AreaChart data={byDay}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="day" tickLine={false} axisLine={false} />
                            <YAxis tickLine={false} axisLine={false} width={36} />
                            <Tooltip />
                            <Area type="monotone" dataKey="count" name="Заказы" stroke={PALETTE[0]} fill={PALETTE[0]} fillOpacity={0.15} strokeWidth={2} />
                        </AreaChart>
                    </ChartCard>
                </Col>
                <Col span={12}>
                    <ChartCard title="Выручка по дням" empty={byDay.length === 0}>
                        <AreaChart data={byDay}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="day" tickLine={false} axisLine={false} />
                            <YAxis tickLine={false} axisLine={false} width={36} />
                            <Tooltip />
                            <Area type="monotone" dataKey="sum" name="Сумма" stroke={PALETTE[3]} fill={PALETTE[3]} fillOpacity={0.15} strokeWidth={2} />
                        </AreaChart>
                    </ChartCard>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={8}>
                    <ChartCard title="По дням недели" empty={byWeekday.length === 0}>
                        <BarChart data={byWeekday}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="label" tickLine={false} axisLine={false} />
                            <YAxis tickLine={false} axisLine={false} width={36} />
                            <Tooltip />
                            <Bar dataKey="count" name="Заказы" fill={PALETTE[0]} radius={[4, 4, 0, 0]} maxBarSize={36} />
                        </BarChart>
                    </ChartCard>
                </Col>
                <Col span={8}>
                    <ChartCard title="По источнику" empty={(data?.by_source ?? []).length === 0}>
                        <PieChart>
                            <Tooltip />
                            <Legend />
                            <Pie data={data?.by_source ?? []} dataKey="count" nameKey="key" outerRadius={90} stroke="var(--surface-color)" strokeWidth={2} label>
                                {(data?.by_source ?? []).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                            </Pie>
                        </PieChart>
                    </ChartCard>
                </Col>
                <Col span={8}>
                    <ChartCard title="Доставка / самовывоз" empty={(data?.by_delivery_type ?? []).length === 0}>
                        <PieChart>
                            <Tooltip />
                            <Legend />
                            <Pie data={data?.by_delivery_type ?? []} dataKey="count" nameKey="key" outerRadius={90} stroke="var(--surface-color)" strokeWidth={2} label>
                                {(data?.by_delivery_type ?? []).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                            </Pie>
                        </PieChart>
                    </ChartCard>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={8}>
                    <ChartCard title="По способу оплаты" empty={(data?.by_payment_type ?? []).length === 0}>
                        <BarChart data={data?.by_payment_type ?? []}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="key" tickLine={false} axisLine={false} />
                            <YAxis tickLine={false} axisLine={false} width={36} />
                            <Tooltip />
                            <Bar dataKey="count" name="Заказы" fill={PALETTE[4]} radius={[4, 4, 0, 0]} maxBarSize={36} />
                        </BarChart>
                    </ChartCard>
                </Col>
                <Col span={8}>
                    <ChartCard title="По филиалу" empty={(data?.by_place ?? []).length === 0}>
                        <BarChart data={data?.by_place ?? []}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="key" tickLine={false} axisLine={false} />
                            <YAxis tickLine={false} axisLine={false} width={36} />
                            <Tooltip />
                            <Bar dataKey="count" name="Заказы" fill={PALETTE[3]} radius={[4, 4, 0, 0]} maxBarSize={36} />
                        </BarChart>
                    </ChartCard>
                </Col>
                <Col span={8}>
                    <ChartCard title="Какой это по счёту заказ у клиента" empty={(data?.repeat_purchases ?? []).length === 0}>
                        <BarChart data={data?.repeat_purchases ?? []}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="label" tickLine={false} axisLine={false} />
                            <YAxis tickLine={false} axisLine={false} width={36} />
                            <Tooltip />
                            <Bar dataKey="count" name="Клиентов" fill={PALETTE[5]} radius={[4, 4, 0, 0]} maxBarSize={36} />
                        </BarChart>
                    </ChartCard>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <div className="oa-card">
                        <div className="oa-card__head"><h3 className="oa-card__title">Топ товаров</h3></div>
                        <Table
                            size="small"
                            pagination={false}
                            dataSource={data?.top_products ?? []}
                            rowKey="product_id"
                            columns={[
                                { title: 'ID товара', dataIndex: 'product_id' },
                                { title: 'Кол-во', dataIndex: 'quantity', render: (v: number) => v.toFixed(1) },
                                { title: 'Выручка', dataIndex: 'revenue', render: money },
                                { title: 'Заказов', dataIndex: 'orders_count' },
                            ]}
                        />
                    </div>
                </Col>
                <Col span={12}>
                    <div className="oa-card">
                        <div className="oa-card__head"><h3 className="oa-card__title">Топ скидок и купонов</h3></div>
                        <Table
                            size="small"
                            pagination={false}
                            dataSource={data?.top_discounts ?? []}
                            rowKey={(r) => `${r.type}_${r.code}_${r.name}`}
                            columns={[
                                { title: 'Тип', dataIndex: 'type', render: (v: string) => <Tag color={v === 'coupon' ? 'gold' : 'blue'}>{v === 'coupon' ? 'купон' : 'авто'}</Tag> },
                                { title: 'Код', dataIndex: 'code' },
                                { title: 'Название', dataIndex: 'name' },
                                { title: 'Кол-во', dataIndex: 'count' },
                                { title: 'Сумма', dataIndex: 'amount', render: money },
                            ]}
                        />
                    </div>
                </Col>
            </Row>
        </div>
    )
})
